import { yupResolver } from '@hookform/resolvers/yup'
import { find, forEach, map } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { MultiSelect, MultiSelectGroup } from '@/components/multi-select'
import SelectField from '@/components/select-field'
import {
  Button,
  Checkbox,
  DateTimePicker,
  Input,
  Modal,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@/components/ui'
import Loader from '../../../common/components/Loader'
import {
  NotificationPayload,
  Notifications,
  SettingsGender,
} from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { Countries } from '../../../core/constants/countries'
import { getArticles } from '../../../core/services/articles'
import { getAllArtists } from '../../../core/services/artist'
import { getSettingsGender } from '../../../core/services/festival-settings'
import { getAllInfos } from '../../../core/services/infos'
import {
  createNotification,
  getAllNotifications,
} from '../../../core/services/notifications'
import { getAllStages } from '../../../core/services/stage'
import {
  getAllCategories,
  getTicketsByCategoryId,
} from '../../../core/services/tickets'
import { getPhotos, getVideos } from '../../../core/services/video'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'

interface IValues {
  festivalEditionId: string
  title: string
  text: string
  forIOS: boolean
  forAndroid: boolean
  activeSegments: boolean
  segmentContent?: {
    title: string
    allCheck: boolean
    options: any[]
  }[]
  immediate: string
  time?: string
  otherSettings: 'launchUrl' | 'category'
  launchUrl?: string | null
  category?:
    | 'TICKETS'
    | 'ARTISTS'
    | 'NEWS'
    | 'STAGES'
    | 'VIDEO_GALLERY'
    | 'PHOTO_GALLERY'
    | 'INFO'
    | 'MAP'
    | 'MY_WALLET'
    | null
  categoryDetails?: {
    label: string
    value: string
  } | null
}

interface Options {
  label: string
  value: string
}

const toLocalDateTimeInput = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export interface CreateEditModalProps {
  visible: boolean
  setVisible: (show: boolean) => void
  setNotifications: (n: Notifications[]) => void
}

export const CreateEditModal = (props: CreateEditModalProps): JSX.Element => {
  const { visible, setVisible, setNotifications } = props

  const [gender, setGender] = useState<SettingsGender[]>([])
  const checkinOptions = [
    { label: 'No Check-in', value: 'NO_CHECKIN' },
    { label: 'Check-in Done', value: 'CHECKIN_DONE' },
  ]
  const topUpOptions = [
    { label: 'My Balance Amount = 0', value: 'AMOUNT_ZERO' },
    { label: 'My Balance Amount = +', value: 'POSITIVE_AMOUNT ' },
  ]
  const [tickets, setTickets] = useState<MultiSelectGroup[]>([])
  const [deleteIndex, setDeleteIndex] = useState<number>(-1)
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const { editionId } = useParams()
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([])

  const getCategoryOptions = async (type: string) => {
    setCategoryOptions([])
    setValue('categoryDetails', null)
    if (type === 'ARTISTS') {
      setIsLoading(true)
      const resp = await getAllArtists({ size: 1000 })
      if (resp.status === 200) {
        setCategoryOptions(
          resp.data.content.map((artist) => ({
            label: artist.name,
            value: artist.id,
          })),
        )
      }
      setIsLoading(false)
    }
    if (type === 'STAGES') {
      setIsLoading(true)
      const resp = await getAllStages({ size: 1000 })
      if (resp.status === 200) {
        setCategoryOptions(
          resp.data.content.map((stage) => ({
            label: stage.name,
            value: stage.id,
          })),
        )
      }
      setIsLoading(false)
    }
    if (type === 'NEWS') {
      setIsLoading(true)
      const resp = await getArticles({ size: 1000 })
      if (resp.status === 200) {
        setCategoryOptions(
          resp.data.content.map((news) => ({
            label: news.title,
            value: news.id,
          })),
        )
      }
      setIsLoading(false)
    }
    if (type === 'INFO') {
      setIsLoading(true)
      const resp = await getAllInfos({ size: 1000 })
      if (resp.status === 200) {
        setCategoryOptions(
          resp.data.content.map((info) => ({
            label: info.title,
            value: info.id,
          })),
        )
      }
      setIsLoading(false)
    }
    if (type === 'VIDEO_GALLERY') {
      setIsLoading(true)
      const resp = await getVideos(editionId as string, { size: 1000 })
      if (resp.status === 200) {
        setCategoryOptions(
          resp.data.content.map((video) => ({
            label: video.name,
            value: video.id,
          })),
        )
      }
      setIsLoading(false)
    }
    if (type === 'PHOTO_GALLERY') {
      setIsLoading(true)
      const resp = await getPhotos(editionId as string, { size: 1000 })
      if (resp.status === 200) {
        setCategoryOptions(
          resp.data.content.map((photo) => ({
            label: photo.link,
            value: photo.id,
          })),
        )
      }
      setIsLoading(false)
    }
  }

  const getModalData = useCallback(async () => {
    if (!visible) return
    try {
      setIsLoading(true)
      const resp = await getSettingsGender()
      if (resp.status === 200) {
        setGender(resp.data)
      }
      const response2 = await getAllCategories({ size: 100 })
      if (response2.status === 200) {
        const temp: {
          label: string
          options: {
            label: string
            value: string
          }[]
        }[] = []
        map(response2.data.content, async (category) => {
          const resp2 = await getTicketsByCategoryId(category.id)
          if (resp2.status === 200) {
            const temp2: { label: string; value: string }[] = []
            map(resp2.data.content, (ticket) => {
              temp2.push({ label: ticket.name, value: ticket.id })
            })
            temp.push({ label: category.name, options: temp2 })
          }
        })
        setTickets(temp)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setIsLoading(false)
    }
  }, [visible])

  useEffect(() => {
    getModalData()
    return () => {
      reset()
    }
  }, [getModalData])

  const handleClose = () => {
    setVisible(false)
  }

  const schema = yup.object().shape({
    festivalEditionId: yup.string().required(),
    title: yup.string().required(),
    text: yup.string().required(),
    forIOS: yup.boolean().required(),
    forAndroid: yup.boolean().required(),
    activeSegments: yup.boolean().required(),
    segmentContent: yup
      .array()
      .of(
        yup.object().shape({
          title: yup.string().required('Field is required'),
          allCheck: yup.boolean().required('Field is required'),
          options: yup.array().required('Field is required'),
        }),
      )
      .when('activeSegments', {
        is: (val: string) => (val ? true : false),
        then: () => yup.array().required(),
        otherwise: () => yup.array().nullable().optional(),
      }),
    immediate: yup.string().required(),
    time: yup.string().when('immediate', {
      is: (val: string) => (val === 'true' ? true : false),
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) => schema.required(),
    }),
    otherSettings: yup.string().oneOf(['launchUrl', 'category']).required(),
    launchUrl: yup.string().when('otherSettings', {
      is: (val: string) => (val === 'launchUrl' ? true : false),
      then: () => yup.string().required(),
      otherwise: () => yup.string().nullable().optional(),
    }),
    category: yup
      .string()
      .oneOf([
        'TICKETS',
        'ARTISTS',
        'NEWS',
        'STAGES',
        'VIDEO_GALLERY',
        'PHOTO_GALLERY',
        'INFO',
        'MAP',
        'MY_WALLET',
      ])
      .when('otherSettings', {
        is: (val: string) => (val === 'launchUrl' ? true : false),
        then: () => yup.string().nullable().optional(),
        otherwise: () => yup.string().required(),
      }),
    categoryDetails: yup
      .object()
      .shape({
        label: yup.string().required(),
        value: yup.string().required(),
      })
      .when(['otherSettings', 'category'], {
        is: (otherSettings: string, category: string) => {
          return (
            otherSettings === 'category' &&
            !['MAP', 'MY_WALLET'].includes(category)
          )
        },
        then: () =>
          yup.object().shape({
            label: yup.string().required(),
            value: yup.string().required(),
          }),
        otherwise: () => yup.string().nullable().optional(),
      }),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    reset,
    setValue,
    control,
    watch,
  } = useForm<IValues>({
    mode: 'onChange',
    resolver: yupResolver<IValues>(schema),
    defaultValues: {
      immediate: undefined,
      festivalEditionId: editionId,
    },
  })

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const submit = async (values: IValues) => {
    try {
      setIsSubmitting(true)
      const payload: NotificationPayload = {
        festivalEditionId: values.festivalEditionId,
        title: values.title,
        text: values.text,
        forIOS: values.forIOS,
        forAndroid: values.forAndroid,
        activeSegments: values.activeSegments,
        immediately: values.immediate === 'true' ? true : false,
        time: values.time ? new Date(values.time).valueOf() : null,
        launchUrl:
          values.otherSettings === 'launchUrl' && values.launchUrl
            ? values.launchUrl
            : null,
        category:
          values.otherSettings === 'category' && values.category
            ? values.category
            : null,
        categoryDetails:
          values.otherSettings === 'category' && values.categoryDetails
            ? values.categoryDetails.value
            : null,
      }
      forEach(values.segmentContent, (segment) => {
        if (segment.title === 'Country') {
          payload.countrySegment = {
            allCountries: segment.allCheck,
            countries: segment.options.map((o) => o.value),
          }
        }
        if (segment.title === 'Age') {
          payload.ageSegment = {
            allAges: segment.allCheck,
            ages: segment.options.map((o) => o.value),
          }
        }
        if (segment.title === 'Gender') {
          payload.genderSegment = {
            allGenders: segment.allCheck,
            genders: segment.options.map((o) => o.value),
          }
        }
        if (segment.title === 'Check-in') {
          payload.checkInSegment = {
            allCheckIns: segment.allCheck,
            checkInTypes: segment.options.map((o) => o.value),
          }
        }
        if (segment.title === 'Top-Up') {
          payload.topUpSegment = {
            allTopUps: segment.allCheck,
            topUpTypes: segment.options.map((o) => o.value),
          }
        }
        if (segment.title === 'Type of Ticket') {
          payload.ticketTypeSegment = {
            allTickets: segment.allCheck,
            tickets: segment.options.map((o) => o.value),
          }
        }
      })
      const { status } = await createNotification(payload)
      const resp = await getAllNotifications(editionId || '')
      if (status === 204 && resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Create Successful')
        setNotifications(resp.data)
        handleClose()
      }
    } catch (e) {
      setIsError(true)
      setShowMessageBox(true)
      setMessage('Failed to create notification')
    } finally {
      setIsSubmitting(false)
    }
  }

  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'segmentContent', // unique name for your Field Array
  })

  const immediate = watch('immediate')
  const otherSettings = watch('otherSettings')
  const segmentWatch = watch('activeSegments')
  const segmentContentWatch = watch('segmentContent')
  const watchCategory = watch('category')
  const watchTime = watch('time')
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  } as const

  const getCheckboxText = (title: string) => {
    switch (title) {
      case 'Country':
        return 'Countries'
      case 'Age':
        return 'Ages'
      case 'Gender':
        return 'Genders'
      case 'Check-in':
        return 'Check-ins'
      case 'Top-Up':
        return 'Top-Ups'
      case 'Type of Ticket':
        return 'Type of Tickets'
      default:
        return ''
    }
  }

  const getOptions = (title: string): Options[] => {
    switch (title) {
      case 'Country':
        return map(Countries, (country) => {
          return { label: country.countryNameEn, value: country.countryCode }
        })
      case 'Age':
        return Array.from(Array(100).keys()).map((i) => {
          return { label: i.toString(), value: i.toString() }
        })
      case 'Gender':
        return map(gender, (g) => {
          return { label: g.name, value: g.id }
        })
      case 'Check-in':
        return checkinOptions.map((c) => {
          return { label: c.label, value: c.value }
        })
      case 'Top-Up':
        return topUpOptions.map((c) => {
          return { label: c.label, value: c.value }
        })
      default:
        return []
    }
  }

  const ticketOptions = useMemo(() => {
    return tickets.flatMap((group) =>
      group.options.map((option) => ({
        ...option,
        label: `${group.label} - ${option.label}`,
      })),
    )
  }, [tickets])

  return (
    <>
      {(isSubmitting || isLoading) && <Loader />}
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Notifications" subtitle="Add New Notification" />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              form="notification-form"
              type="submit"
              variant="primary"
              disabled={!isValid}
              loading={isSubmitting}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="notification-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Notification Title" error={errors?.title?.message}>
            <Input
              isInvalid={!!errors?.title?.message}
              {...register('title')}
              type="text"
              placeholder="Enter Title"
            />
          </FormGroup>
          <FormGroup label="Notification text" error={errors?.text?.message}>
            <Textarea
              isInvalid={!!errors?.text?.message}
              {...register('text')}
              rows={3}
              placeholder="Enter Text"
            />
          </FormGroup>
          <FormGroup label="Target">
            <div className="flex flex-col gap-2">
              <Controller
                name="forIOS"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm text-monochrome-2">
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                    iOS
                  </label>
                )}
              />
              <Controller
                name="forAndroid"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm text-monochrome-2">
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                    Android
                  </label>
                )}
              />
            </div>
          </FormGroup>
          <FormGroup label="Segments (If no selection were made, notification will be sent to all countries, ages, genders, check-ins, top-ups)">
            <div className="flex w-full flex-col gap-2">
              <Controller
                name="activeSegments"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm text-monochrome-2">
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                    Active
                  </label>
                )}
              />
              {segmentWatch &&
                map(fields, (field, index) => {
                  const usedTitles = (segmentContentWatch || [])
                    .map((segment, segmentIndex) =>
                      segmentIndex === index ? null : segment?.title,
                    )
                    .filter(Boolean)
                  return (
                    <div
                      key={field.id}
                      className="flex w-full flex-col gap-2 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
                    >
                      <FormGroup
                        label="Type"
                        error={errors?.segmentContent?.[index]?.title?.message}
                      >
                        <SelectField
                          placeholder="Select Segment Type"
                          value={segmentContentWatch?.[index]?.title || 'none'}
                          onValueChange={(value) => {
                            setValue(`segmentContent.${index}.allCheck`, false)
                            setValue(`segmentContent.${index}.options`, [])
                            setValue(`segmentContent.${index}.title`, value)
                          }}
                          options={[
                            {
                              value: 'none',
                              label: 'Select Segment Type',
                              disabled: true,
                            },
                            {
                              value: 'Country',
                              label: 'Country',
                              disabled: usedTitles.includes('Country'),
                            },
                            {
                              value: 'Age',
                              label: 'Age',
                              disabled: usedTitles.includes('Age'),
                            },
                            {
                              value: 'Gender',
                              label: 'Gender',
                              disabled: usedTitles.includes('Gender'),
                            },
                            {
                              value: 'Check-in',
                              label: 'Check-in',
                              disabled: usedTitles.includes('Check-in'),
                            },
                            {
                              value: 'Top-Up',
                              label: 'Top-Up',
                              disabled: usedTitles.includes('Top-Up'),
                            },
                            {
                              value: 'Type of Ticket',
                              label: 'Type of Ticket',
                              disabled: usedTitles.includes('Type of Ticket'),
                            },
                          ]}
                          error={
                            errors?.segmentContent?.[index]?.title?.message
                          }
                          outline
                        />
                      </FormGroup>
                      <FormGroup
                        error={
                          errors?.segmentContent?.[index]?.allCheck?.message
                        }
                      >
                        <Controller
                          name={`segmentContent.${index}.allCheck`}
                          control={control}
                          render={({ field: segmentField }) => (
                            <label className="flex items-center gap-2 text-sm text-monochrome-2">
                              <Checkbox
                                checked={Boolean(segmentField.value)}
                                onCheckedChange={(checked) => {
                                  segmentField.onChange(Boolean(checked))
                                  if (checked) {
                                    setValue(
                                      `segmentContent.${index}.options`,
                                      [],
                                    )
                                  }
                                }}
                              />
                              {`All ${
                                segmentContentWatch
                                  ? getCheckboxText(
                                      segmentContentWatch?.[index]?.title,
                                    )
                                  : ''
                              }`}
                            </label>
                          )}
                        />
                      </FormGroup>
                      <FormGroup id={`select-dropdown-${index}`}>
                        <Controller
                          control={control}
                          render={({ field }) => (
                            <MultiSelect
                              options={
                                segmentContentWatch &&
                                segmentContentWatch?.[index].title !==
                                  'Type of Ticket'
                                  ? getOptions(
                                      segmentContentWatch?.[index]?.title,
                                    )
                                  : tickets
                              }
                              value={field.value || []}
                              onChange={(next) => {
                                setValue(
                                  `segmentContent.${index}.options`,
                                  next,
                                )
                              }}
                              placeholder={
                                segmentContentWatch?.[index]?.title ||
                                'Select options'
                              }
                              disabled={watch(
                                `segmentContent.${index}.allCheck`,
                              )}
                            />
                          )}
                          name={`segmentContent.${index}.options`}
                        />
                      </FormGroup>
                      <div className="flex justify-end w-full">
                        <Button
                          variant="secondary"
                          type="button"
                          className="bg-accent-3 hover:bg-accent-3/90"
                          onClick={() => {
                            setDeleteIndex(index)
                            setDeleteConfirmationModal(true)
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )
                })}
              <Button
                variant="secondary"
                className="w-full"
                type="button"
                disabled={!segmentWatch || fields.length > 5}
                onClick={() =>
                  append({ allCheck: false, title: '', options: [] })
                }
              >
                Add New
              </Button>
            </div>
          </FormGroup>
          <FormGroup label="Select Time">
            <div className="flex flex-col gap-2 w-full">
              <Controller
                name="immediate"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="gap-2"
                  >
                    <label className="flex items-center gap-2 text-sm text-monochrome-2">
                      <RadioGroupItem value="true" />
                      Immediately
                    </label>
                    <label className="flex items-center gap-2 text-sm text-monochrome-2">
                      <RadioGroupItem value="false" />
                      Specific Time
                    </label>
                  </RadioGroup>
                )}
              />
              {immediate === 'false' && (
                <Controller
                  name="time"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      value={field.value ? new Date(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date ? toLocalDateTimeInput(date) : '')
                      }
                      placeholder="Select Time"
                      hasError={!!errors?.time?.message}
                    />
                  )}
                />
              )}
              {immediate === 'false' && !!watchTime && (
                <div
                  className="rounded-2xl border px-4 py-3 text-sm"
                  style={{ background: '#CFF4FC', borderColor: '#B6EFFB' }}
                >
                  <span style={{ color: '#055160' }}>
                    {`Message will start sending ${new Date(
                      watchTime,
                    ).toLocaleDateString('en-US', options)}`}
                  </span>
                </div>
              )}
            </div>
          </FormGroup>
          <FormGroup label="Other Settings">
            <div className="flex flex-col gap-2 w-full">
              <Controller
                name="otherSettings"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="gap-2"
                  >
                    <label className="flex items-center gap-2 text-sm text-monochrome-2">
                      <RadioGroupItem value="launchUrl" />
                      Launch URL
                    </label>
                    {otherSettings === 'launchUrl' && (
                      <Input
                        isInvalid={!!errors?.launchUrl?.message}
                        {...register('launchUrl')}
                        type="text"
                        placeholder="Launch URL"
                        className="mt-2"
                      />
                    )}
                    <label className="flex items-center gap-2 text-sm text-monochrome-2">
                      <RadioGroupItem value="category" />
                      Category
                    </label>
                  </RadioGroup>
                )}
              />
              {otherSettings === 'category' && (
                <>
                  <Controller
                    control={control}
                    name="category"
                    render={({ field }) => (
                      <SelectField
                        placeholder="Select Category"
                        value={field.value || 'none'}
                        onValueChange={(value) => {
                          field.onChange(value)
                          getCategoryOptions(value)
                        }}
                        options={[
                          {
                            value: 'none',
                            label: 'Select Category',
                            disabled: true,
                          },
                          { value: 'TICKETS', label: 'Tickets' },
                          { value: 'ARTISTS', label: 'Artists' },
                          { value: 'NEWS', label: 'News' },
                          { value: 'STAGES', label: 'Stages' },
                          { value: 'VIDEO_GALLERY', label: 'Video Gallery' },
                          { value: 'PHOTO_GALLERY', label: 'Photo Gallery' },
                          { value: 'INFO', label: 'Info' },
                          { value: 'MAP', label: 'Map' },
                          { value: 'MY_WALLET', label: 'My Wallet' },
                        ]}
                        error={errors?.category?.message}
                        outline
                      />
                    )}
                  />
                  {watchCategory === 'TICKETS' && (
                    <Controller
                      control={control}
                      name="categoryDetails"
                      render={({ field }) => (
                        <SelectField
                          placeholder="Select Ticket"
                          value={field.value?.value || 'none'}
                          onValueChange={(value) => {
                            const selected =
                              ticketOptions.find(
                                (option) => option.value === value,
                              ) ?? null
                            field.onChange(selected)
                          }}
                          options={[
                            {
                              value: 'none',
                              label: 'Select Ticket',
                              disabled: true,
                            },
                            ...ticketOptions,
                          ]}
                          error={errors?.categoryDetails?.message}
                          outline
                        />
                      )}
                    />
                  )}
                  {!!watchCategory &&
                    !['MAP', 'MY_WALLET', 'TICKETS'].includes(
                      watchCategory,
                    ) && (
                      <Controller
                        control={control}
                        name="categoryDetails"
                        render={({ field }) => (
                          <SelectField
                            placeholder={`Select ${watchCategory}`}
                            value={field.value?.value || 'none'}
                            onValueChange={(value) => {
                              const selected =
                                categoryOptions.find(
                                  (option) => option.value === value,
                                ) ?? null
                              field.onChange(selected)
                            }}
                            options={[
                              {
                                value: 'none',
                                label: `Select ${watchCategory}`,
                                disabled: true,
                              },
                              ...categoryOptions,
                            ]}
                            error={errors?.categoryDetails?.message}
                            outline
                          />
                        )}
                      />
                    )}
                </>
              )}
            </div>
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Segment'}
        deleteFunc={() => remove(deleteIndex)}
      />
    </>
  )
}
